import { createAsyncThunk, type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit';

import axiosInstance from '@/utils/axios';
import { fetchProfile } from '../profile/profile-api';
import { fetchUserPermissions } from './permissions-api';
import { AnalyticsEvent, resolveAnalyticsHospitalId, trackAnalyticsEvent } from '@/services/analytics';
import TokenService from '@/services/token.service';
import type {
    ForgotPasswordRequest,
    LoginCredentials,
    LoginMfaResponse,
    ResetPasswordRequest,
    User,
    VerifyMfaResponse
} from '@/types/auth';
import { getErrorMessage } from '@/utils/error-message';

type LoginResult = {
    email: string | null;
    session: string | null;
    password: string | null;
};

type InitializeAuthResult = {
    user: User | null;
    isLoggedIn: boolean;
};

type SessionDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

/** Profile and permissions load together. A permissions failure leaves an empty list and does not block sign-in. */
const loadProfileAndPermissions = async (dispatch: SessionDispatch): Promise<User> => {
    const profilePromise = dispatch(fetchProfile()).unwrap();
    const permissionsPromise = dispatch(fetchUserPermissions());
    try {
        const user = await profilePromise;
        await permissionsPromise;
        return user;
    } catch (error) {
        await permissionsPromise;
        throw error;
    }
};

export const initializeAuth = createAsyncThunk<InitializeAuthResult>('auth/initializeAuth', async (_, { dispatch }) => {
    const refreshToken = TokenService.getRefreshToken();
    if (!refreshToken) {
        return { user: null, isLoggedIn: false };
    }

    try {
        const accessToken = await TokenService.refreshAccessToken();
        if (!accessToken) {
            return { user: null, isLoggedIn: false };
        }
        const user = await loadProfileAndPermissions(dispatch);
        return { user, isLoggedIn: true };
    } catch {
        TokenService.clearTokens();
        return { user: null, isLoggedIn: false };
    }
});

export const login = createAsyncThunk<LoginResult, LoginCredentials, { rejectValue: string }>(
    'auth/login',
    async (credentials, { rejectWithValue }) => {
        try {
            const { data } = await axiosInstance.post<LoginMfaResponse>('/auth/login/mfa', credentials);
            if (!data) {
                return rejectWithValue('Invalid login response');
            }

            if (!data.session) {
                return rejectWithValue('Session missing from login response');
            }

            return {
                email: credentials.email ?? null,
                password: credentials.password ?? null,
                session: data.session
            };
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to sign in'));
        }
    }
);

export const verifyMfa = createAsyncThunk<
    { user: User; token: string },
    { email: string; session: string; otp: string },
    { rejectValue: string }
>('auth/verifyMfa', async ({ email, session, otp }, { dispatch, rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.post<VerifyMfaResponse>('/auth/confirm/mfa', {
            email,
            challengeName: 'EMAIL_OTP',
            session,
            code: otp
        });
        const { accessToken, refreshToken } = data;

        if (!accessToken) {
            return rejectWithValue('Access token missing from MFA verification response');
        }

        TokenService.setAccessToken(accessToken);
        if (refreshToken) {
            TokenService.setRefreshToken(refreshToken);
        }

        let user: User;
        try {
            user = await loadProfileAndPermissions(dispatch);
        } catch (error) {
            TokenService.clearTokens();
            throw error;
        }

        void trackAnalyticsEvent({
            event: AnalyticsEvent.LoginSucceeded,
            hospitalId: resolveAnalyticsHospitalId(user)
        });

        return { user, token: accessToken };
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to verify OTP'));
    }
});

export const resendCode = createAsyncThunk<
    { message: string; session: string },
    { email: string; password: string },
    { rejectValue: string }
>('auth/resendCode', async ({ email, password }, { rejectWithValue }) => {
    try {
        const { data } = await axiosInstance.post<{ message: string; session: string }>('/auth/resend-code', { email, password });
        return data;
    } catch (error: unknown) {
        return rejectWithValue(getErrorMessage(error, 'Failed to resend verification code'));
    }
});

export const forgotPassword = createAsyncThunk<void, ForgotPasswordRequest, { rejectValue: string }>(
    'auth/forgotPassword',
    async (payload, { rejectWithValue }) => {
        try {
            // Endpoint can be updated later based on backend contract.
            await axiosInstance.post('/auth/password/forgot', payload);
            return undefined;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to send reset password email'));
        }
    }
);

export const resetPassword = createAsyncThunk<void, ResetPasswordRequest, { rejectValue: string }>(
    'auth/resetPassword',
    async (payload, { rejectWithValue }) => {
        try {
            // Endpoint can be updated later based on backend contract.
            await axiosInstance.post('/auth/password/reset', payload);
            return undefined;
        } catch (error: unknown) {
            return rejectWithValue(getErrorMessage(error, 'Failed to reset password'));
        }
    }
);
